import React, { createContext, useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSingleLanguage, setIsSingleLanguage] = useState(true);
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(localStorage.getItem("auth-token") || "");
  const [socket, setSocket] = useState(null); // Add socket state
  const [versions, setVersions] = useState([]); // Supported versions from the server
  const [defaultVersion, setDefaultVersion] = useState(null);
  const langMap = { English: "EU", Korean: "KR", Russian: "RU" };
  const updateToastId = useRef(null);
  const currentRequestIdRef = useRef(null);

  const isDev = process.env.NODE_ENV === "development";
  const socketRef = useRef(null);

  const updateDatabase = async (ver, force) => {
    console.log("Updating database... ver:", ver, "force:", force);
    if (!socketRef.current) {
      console.error("Socket not available for updateDatabase");
      return;
    }
    if (force) {
      socketRef.current.emit("forceUpdateDB", { ver });
    } else {
      socketRef.current.emit("updateDB", { ver });
    }
  };

  useEffect(() => {
    // Server URL is configurable via env; falls back to the previous hardcoded
    // dev/prod hosts so existing setups keep working without a .env file.
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL ||
      (isDev ? "http://localhost:5000" : "https://insc.vtrialonis.com");
    const socketPath =
      process.env.REACT_APP_SOCKET_PATH || (isDev ? undefined : "/socket.io");

    socketRef.current = io(socketUrl, socketPath ? { path: socketPath } : {});

    socketRef.current.on("connect", () => {
      console.log("Socket connected:", socketRef.current.id);
      setSocket(socketRef.current); // Update socket state
      setIsSocketReady(true);
      // Pull the supported version list (single source of truth on the server)
      socketRef.current.emit("reqVersions");
    });

    socketRef.current.on("versions", (data) => {
      setVersions(data?.versions || []);
      setDefaultVersion(data?.defaultVersion ?? null);
    });

    socketRef.current.on("disconnect", () => {
      console.log("Socket disconnected.");
      setSocket(null); // Clear socket state
      setIsSocketReady(false);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const handleToggleChange = () => setIsSingleLanguage(!isSingleLanguage);

  // Single logout used by every screen's NavBar. Previously only Home defined a
  // local one, so TransTool/Differ passed `undefined` to NavBar's logout button.
  const logout = () => {
    setToken("");
    setUsername("");
    setIsAuthenticated(false);
    localStorage.removeItem("auth-token");
    localStorage.removeItem("username");
  };

  return (
    <GlobalContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        isSingleLanguage,
        handleToggleChange,
        username,
        setUsername,
        password,
        setPassword,
        langMap,
        socket, // Use state instead of socketRef.current
        isSocketReady,
        currentRequestIdRef,
        toast,
        logout,
        versions,
        defaultVersion,
        updateDatabase,
        updateToastId,
        isDev,
        setToken,
        token,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};